// src/pages/CsCardPage.tsx
//
// ★ PHASE 10 — Annex A story 49, "Print CS Card Information". Ministry/Agency
// HR only — see `routes/cs-card.ts`'s own header for exactly what this is
// (a reference print of PRS2's own entitlement holding) and is not (an
// official Civil Service card — PRS2 issues no card number or photograph).
import { useId, useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { ServiceDatePicker } from '../components/ServiceDatePicker'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate } from '../lib/format'

export function CsCardPage() {
  const dateId = useId()
  const [officerId, setOfficerId] = useState('')
  const [asOfDate, setAsOfDate] = useState('')
  const [submittedOfficerId, setSubmittedOfficerId] = useState<string | null>(null)

  const card = useApi(
    () => (submittedOfficerId === null ? Promise.resolve(null) : api.csCard(submittedOfficerId, asOfDate)),
    [submittedOfficerId, asOfDate],
  )

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (officerId.trim().length === 0) return
    setSubmittedOfficerId(officerId.trim())
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Print CS card information</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          This is a REFERENCE PRINT of the entitlement data PRS2 holds for an officer,
          not an official Civil Service card. PRS2 issues no card number, photograph or security feature.
        </p>
      </div>

      <form className="pt-card flex flex-col gap-5" onSubmit={handleSubmit}>
        <SgdsInput
          label="Officer ID"
          value={officerId}
          onSgdsInput={(e) => setOfficerId(targetValue(e))}
          placeholder="e.g. OFF-02"
        />
        <div>
          <span id={dateId} className="block text-sm font-semibold mb-1">
            As at date (defaults to today if left blank)
          </span>
          <ServiceDatePicker value={asOfDate} onChange={setAsOfDate} labelledBy={dateId} />
        </div>
        <div>
          <SgdsButton type="submit" variant="primary" disabled={officerId.trim().length === 0} loading={card.loading}>
            Look up
          </SgdsButton>
        </div>
      </form>

      {card.error !== null && <ErrorCard title="Could not load CS card information" message={card.error} />}

      {/* ★ WAVE 26 — the wait and the empty answer are STATED. Until now the only
          signal between "Look up" and the card was the button spinner, and a null
          card left the form unchanged with no statement either way. */}
      {submittedOfficerId !== null && card.loading && <LoadingCard label="Looking up the CS card information" rows={3} />}
      {submittedOfficerId !== null && !card.loading && card.error === null && card.data === null && (
        <EmptyCard
          title="No CS card information for this officer"
          message={`Nothing is held for officer ${submittedOfficerId} on that date. Check the officer id; the lookup asked the server and it answered with no card.`}
        />
      )}

      {submittedOfficerId !== null && card.data !== undefined && card.data !== null && (
        <div className="pt-card max-w-lg">
          <p className="text-xs uppercase tracking-wide text-[var(--sgds-body-color-subtle)] mb-3">
            Reference print, not an official CS card
          </p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Officer ID</dt>
            <dd className="pt-tabular">{card.data.officerId}</dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Name</dt>
            <dd>{card.data.displayName}</dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Agency</dt>
            <dd>
              {card.data.agencyName} ({card.data.agencyCode})
            </dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Benefit scheme</dt>
            <dd>{card.data.scheme ?? 'none in force'}</dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Ward class</dt>
            <dd>{card.data.wardClass ?? 'Not applicable'}</dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Entitlement window</dt>
            <dd>
              {card.data.entitlementFrom !== null ? formatIsoDate(card.data.entitlementFrom) : 'Not recorded'} to{' '}
              {card.data.entitlementUntil !== null ? formatIsoDate(card.data.entitlementUntil) : 'open-ended'}
            </dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">As at</dt>
            <dd>{formatIsoDate(card.data.asOfDate)}</dd>
          </dl>

          <div className="flex gap-2 mt-5 pt-no-print">
            <SgdsButton
              variant="outline"
              type="button"
              onClick={() => (window.location.href = api.csCardExportUrl(card.data!.officerId, 'pdf', asOfDate))}
            >
              Print / download PDF
            </SgdsButton>
            <SgdsButton
              variant="outline"
              type="button"
              onClick={() => (window.location.href = api.csCardExportUrl(card.data!.officerId, 'xlsx', asOfDate))}
            >
              Download Excel
            </SgdsButton>
          </div>
        </div>
      )}
    </div>
  )
}
